import React, { useRef, useEffect, useState } from 'react'
import Message from './Message'
import { User } from '../../context/AuthContext'
import { MessageData, FeedbackType, StatusUpdate } from '../../api/chatService'

interface MessageListProps {
  messages: MessageData[]
  isWaiting: boolean
  user: User
  chatId: string
  // Live progress steps for the turn currently being answered.
  statusSteps?: StatusUpdate[]
  onRetry?: () => void
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isWaiting,
  user,
  chatId,
  statusSteps = [],
  onRetry,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevScrollTopRef = useRef<number>(0)
  const [messagesFeedback, setMessagesFeedback] = useState<
    Record<string, FeedbackType | null>
  >({})

  // Initialize feedback state from messages
  useEffect(() => {
    const initialFeedback: Record<string, FeedbackType | null> = {}
    messages.forEach(message => {
      if (message.feedback !== undefined) {
        initialFeedback[message.id] = message.feedback
      }
    })
    setMessagesFeedback(initialFeedback)
  }, [messages])

  const handleFeedback = (messageId: string, feedback: FeedbackType | null) => {
    setMessagesFeedback(prev => ({
      ...prev,
      [messageId]: feedback,
    }))
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      const scrollContainer = messagesEndRef.current.parentElement
      if (scrollContainer) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer
        // Threshold in pixels. If the user is within this many pixels
        // from the bottom, we consider them "at the bottom".
        const scrollThreshold = 100

        // Check if the user is scrolling up
        const isScrollingUp = scrollTop < prevScrollTopRef.current

        // Check if the user is scrolled near the bottom
        const isScrolledToBottom =
          scrollHeight - scrollTop - clientHeight - 80 <= scrollThreshold

        if (isScrolledToBottom && !isScrollingUp) {
          // Calculate the exact scroll position
          const targetScrollTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight

          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          })
        }
      }
    }
    // Update the previous scroll position after checking
    if (messagesEndRef.current && messagesEndRef.current.parentElement) {
      prevScrollTopRef.current = messagesEndRef.current.parentElement.scrollTop
    }
  }, [messages, isWaiting, statusSteps])
  return (
    <div className="message-list">
      {messages.map(message => (
        <Message
          key={message.id}
          role={message.role}
          content={message.content}
          user={user}
          documents={message.documents}
          daxQueries={message.daxQueries}
          files={message.files}
          streamingComplete={message.streamingComplete}
          messageId={message.id}
          chatId={chatId}
          feedback={messagesFeedback[message.id] || message.feedback || null}
          onFeedback={handleFeedback}
          onRetry={
            message.role === 'error' && message.retryable && onRetry
              ? onRetry
              : undefined
          }
        />
      ))}

      {isWaiting && (
        <Message
          role="assistant"
          content=""
          isLoading={true}
          user={user}
          statusSteps={statusSteps}
        />
      )}

      <div ref={messagesEndRef} />
    </div>
  )
}

export default MessageList
